'use strict';
/*

 ============================
 minimal REQUEST for SIMULATE:
 ============================
 {
 "ID":"US1111111", "CountryCode":"US", "SoldToMemberID":"US1111111", "OrderAction":"SIMULATE",
 "Item":[ {"SKU":"01003440","RequestedQuantity":"1"} ]
 }

 */
import ExternalPaymentService from '../payment/externalPaymentService';
import OrderStatusEnum from '../order/OrderStatusEnum.js';
import ShopServiceApiVersion from '../shopServiceApiVersion';
import AdrService from '../adr/AdrService';
import {ConfigService, events, storage} from '@nuskin/ns-util';
import {UserService} from '@nuskin/ns-account';
import {CartService, CheckoutModeService, OrderManager, OrderAdapter, ProcessorCode} from '../shop.js';
import axios from 'axios';

const SERVICE_TIMEOUT = 33000;

//============================
//    Private Functions
//============================
/**
 * Get query params to send on simulate and create calls
 * @param order
 * @returns {{locale: *, signup: *}}
 */
const getParams = (order) => {
    let config = ConfigService.getMarketConfig();
    let params = {
        locale: order.locale,
        signup: CartService.getCartProperty('inSignup')
    };

    if (config.microsite) {
        params.site = config.microsite;
    }
    return params;
};

/**
 * Get headers for simulate and create calls
 * @param {boolean} ignoreOverrides will not set app-setting-overrides header if true
 * @returns {{Accept: string, client_secret: *, client_id: *, "Content-Type": string}}
 */
const _getHeaders = (ignoreOverrides = false) => {
    const appSettingOverrides = storage.getItem(storage.metadata.APP_SETTING_OVERRIDES);
    const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'client_id': ConfigService.getMarketConfig().checkout.clientId,
        'client_secret': ConfigService.getMarketConfig().checkout.clientSecret
    };
    if (UserService.isLoggedIn()) headers.Authorization = `JWT ${UserService.getUser().eid}`;
    // The only app settings that can be overriden are the settings in the order-service group
    if (appSettingOverrides && !ignoreOverrides) headers['App-Setting-Overrides'] = JSON.stringify(appSettingOverrides);
    return headers;
};

/**
 * set the redirect urls on the order object for external payment flow
 * @param salesOrderResponse
 * @param order
 */
const syncRedirectUrls = (salesOrderResponse, order) => {
    order.redirectUrl = salesOrderResponse.redirectUrl;
    order.proxyLaunchUrl = salesOrderResponse.proxyLaunchUrl;
    order.proxyReenterUrl = salesOrderResponse.proxyReenterUrl;
};

/**
 * Sync the data on the salesOrderResponse from SAP back to the order object
 * @param salesOrderResponse
 * @param order
 */
const syncSalesOrderResponse = (salesOrderResponse, order) => {
    const config = ConfigService.getMarketConfig();
    // If using the new vue payment component we get available payments from the payment options service
    if (!config.checkout.useVuePaymentSection) {
        order.availablePayments = salesOrderResponse.availablePayments;
    }
    order.addressTypeList = salesOrderResponse.addressTypeList;
    order.orderProcessingState = salesOrderResponse.orderProcessingState;
};

/**
 * Set paymentAuthorization data in the request from the order object
 * @param order
 * @param requestObject
 */
const populatePaymentAuthorization = (order, requestObject) => {
    if (order.paRes) {
        console.log("Populating paRes...");
        requestObject.paRes = order.paRes;
        requestObject.paymentAuthorizationObject = order.paymentAuthorizationObject;
    }
};

const populateOrderTotals = (order, requestObject) => {
    if (order.orderTotals) {
        // note that it is lowercase 'o'
        requestObject.OrderTotals = {
            "ItemSubtotal": order.orderTotals.itemSubtotal,
            "Shipping": order.orderTotals.shipping,
            "Tax": order.orderTotals.tax,
            "GrandTotal": order.orderTotals.grandTotal,
            "PSV": order.orderTotals.psvTotal,
            "TaxBase": order.orderTotals.taxBase,
            "Savings": order.orderTotals.savings,
            "CV": order.orderTotals.cv,
            "Rounding": order.orderTotals.rounding,
            "TaxInclusive": order.orderTotals.taxInclusive
        };
    }
};

const getScanCards = () => {
    return CartService.getScanCards().map(scanCard => {
        return {
            ScanNumber: scanCard.cardNumber,
            Label: scanCard.cardName,
            Active: "true",
            Action: "ADD"
        }
    });
}

const getResUrl = (isAnonymous = false) => {
    let nsConfig = ConfigService.getMarketConfig(),
        retVal;
    retVal = isAnonymous ?
        ShopServiceApiVersion.getSapOrderServiceVersionedUrl(ConfigService.getMarketConfig().checkout.nsurl_sapOrderService) + '/anonymous' :
        `${nsConfig.awsUrl}/order/v1`;

    return retVal;
};

/**
 * This function is to handle the discontinued SKUs.
 * We extract the SKU first, then remove the product from the cart.
 *
 * @param order The order that contains the cart
 * @param adr The adr information (can be null)
 * @param messages The error messages coming from SAP
 */
const handleDiscontinuedSku = (order, adr, messages) => {
    // Parse the technical message for sku (instead)
    let technicalMessage = messages[0].technicalMessage;

    // Note that in some locale (different language), it does not have the SKU in the message
    if (technicalMessage && technicalMessage.match(/\d{8}/)) {
        let discontinuedSku = technicalMessage.match(/\d{8}/)[0];

        if (discontinuedSku) {
            let cartItems;

            if (adr) {
                if (adr.shipImmediate) {
                    cartItems = CartService.getItemData({cartItems: true});
                } else {
                    cartItems = CartService.getItemData({cartAdrItems: true});
                }
            } else {
                cartItems = CartService.getItemData({cartOrderItems: true});
            }

            // we need to loop thru cartItems to delete the product with a particular SKU
            cartItems.forEach(cartItem => {
                if (cartItem.sku === discontinuedSku) {
                    CartService.removeItemByKey(cartItem.key);
                }
            });
        }
    }
};

const formatDateYYYYMMdd = (dateString) => {
    // dateString can be null/empty
    if (dateString) {
        // dateString should come in as this format: 2015-10-15T11:00:00-07:00
        // So, we just need to wipe out anything starting from 'T'
        let indexToRemove = dateString.indexOf("T");
        if (indexToRemove > -1) {
            dateString = dateString.substring(0, indexToRemove);
        }
    }
    return dateString;
};


/**
 * Handles the ship immediate adr simulate call
 * @param adr
 * @returns {*|Promise<any>}
 */
const adrShipImmediateSimulate = (adr) => {
    let order = OrderManager.getOrder(),
        requestObject = {
            adr: {
                SalesOrder: OrderAdapter.populateSalesOrder('SIMULATE', adr),
                DeliveryDate: formatDateYYYYMMdd(adr.deliveryDate),
                OrderFrequency: adr.orderFrequency
            }
        };
    let promise = new Promise(function(resolve, reject) {
        axios({
            method: 'PUT',
            url: ShopServiceApiVersion.getSapAdrShipImmediateVersionedUrl(ConfigService.getMarketConfig().checkout.nsurl_sapAdrShipImmediateService),
            data: requestObject,
            params: getParams(order),
            headers: _getHeaders(),
            timeout: SERVICE_TIMEOUT
        })
            .then((response) => {
                let data = response.data;
                if (data && data.messages) {
                    order.messages = data.messages;
                }
                if (data && data.SalesOrderResponse && data.SalesOrderResponse.SalesOrderDetail) {
                    syncSalesOrderResponse(data.SalesOrderResponse, order);
                    OrderAdapter.toOrder(data.SalesOrderResponse.SalesOrderDetail, adr);
                } else {
                    console.log("SAP Error while calling simulate...");
                    events.publishError(events.errors.SAP, {messageString: "SAP Error while calling simulate..."});
                }
                resolve(response);
            })
            .catch((err) => {
                console.log(err);
                let data;
                if (err && err.response) data = err.response.data;
                if (data && data.messages) {
                    if ((data.messages[0].type === 'OrderException') && data.SalesOrderResponse && data.SalesOrderResponse.SalesOrderDetail) {
                        syncSalesOrderResponse(data.SalesOrderResponse, order);
                        OrderAdapter.toOrder(data.SalesOrderResponse.SalesOrderDetail, adr);
                    } else if (data.messages[0].type === 'ProductDiscontinuedException') {
                        handleDiscontinuedSku(order, adr, data.messages);
                    }

                }
                if (err.config && err.config.url) {
                    events.publishAjaxError(events.errors.ORDER_SERVICE, {
                        url: err.config.url,
                        response: err
                    });
                }
                reject(err);
            });
    });
    return promise;
};

/**
 * Makes the adr ship immediate create call
 * @param adr
 * @returns {*|Promise<any>}
 */
const adrShipImmediateCreate = (adr) => {
    let order = OrderManager.getOrder(),
        requestObject = {
            adr: {
                SalesOrder: OrderAdapter.populateSalesOrder('CREATE', adr),
                DeliveryDate: formatDateYYYYMMdd(adr.deliveryDate),
                OrderFrequency: adr.orderFrequency,
                Scan: getScanCards()
            }
        };

    // For ship immediate the paymentauthorization go on the root level request object not the sales order.
    // For adr we don't pass the paymentauthorization
    // That is why it isn't in the populateSalesOrder function in the orderAdapter
    populateOrderTotals(order, requestObject);
    populatePaymentAuthorization(order, requestObject);
    let promise = new Promise(function(resolve, reject) {
        return axios({
            method: 'POST',
            url: ShopServiceApiVersion.getSapAdrShipImmediateVersionedUrl(ConfigService.getMarketConfig().checkout.nsurl_sapAdrShipImmediateService),
            data: requestObject,
            params: getParams(order),
            headers: _getHeaders(),
            timeout: SERVICE_TIMEOUT
        })
            .then((response) => {
                let data = response.data;
                if (data && data.AdrShipImmediateResponse && data.AdrShipImmediateResponse.salesOrderResponse) {
                    let salesOrderDetail = data.AdrShipImmediateResponse.salesOrderResponse.SalesOrderDetail;
                    order.paymentAuthorizationObject = data.AdrShipImmediateResponse.paymentAuthorizationObject || null;
                    syncRedirectUrls(data.AdrShipImmediateResponse.salesOrderResponse, order);
                    if (order.paymentAuthorizationObject) {
                        let adrResponse = data.AdrShipImmediateResponse.adrResponse;
                        if (adrResponse && adrResponse.ADRDetail && adrResponse.ADRDetail.SalesOrderDetail) {
                            order.externalPaymentAdrNumber = adrResponse.ADRDetail.SalesOrderDetail.OrderID;
                        }
                    } else {
                        order.externalPaymentAdrNumber = null
                    }

                    if (salesOrderDetail) {
                        OrderAdapter.toOrder(salesOrderDetail, adr, true);
                        order.adrOverrideNumber = salesOrderDetail.ADROverrideNumber;
                    }
                    // Clear locally-cached ADR List
                    AdrService.clearLocalAdrList();
                    // Force a new get adr list call when visiting manage adp page to update the adr list in session storage.
                    sessionStorage.setItem(UserService.getUser().id + '-adr-list-received', 'false');
                } else {
                    console.log("SAP Error while calling create...");
                    events.publishError(events.errors.SAP, {messageString: "SAP Error while calling create..."});
                }
                resolve(response);
            })
            .catch((err) => {
                console.log(err);
                if (err.config && err.config.url) {
                    events.publishAjaxError(events.errors.ORDER_SERVICE, {
                        url: err.config.url,
                        response: err
                    });
                }
                reject(err);
            });
    });
    return promise;
};

/**
 * Make the adr simulate call using the current order
 * @param adr
 * @returns {*|Promise<any>}
 */
const adrSimulate = (adr) => {
    let order = OrderManager.getOrder(),
        requestObject = {
            adr: {
                SalesOrder: OrderAdapter.populateSalesOrder('SIMULATE', adr),
                DeliveryDate: formatDateYYYYMMdd(adr.deliveryDate),
                OrderFrequency: adr.orderFrequency
            }
        };
    let promise = new Promise(function(resolve, reject) {
        return axios({
            method: 'PUT',
            url: ShopServiceApiVersion.getSapAdrServiceVersionedUrl(ConfigService.getMarketConfig().checkout.nsurl_sapAdrService),
            data: requestObject,
            params: getParams(order),
            headers: _getHeaders(),
            timeout: SERVICE_TIMEOUT
        })
            .then((response) => {
                let data = response.data;
                if (data && data.messages) {
                    order.messages = data.messages;
                }
                if (data && data.AdrResponse && data.AdrResponse.ADRDetail && data.AdrResponse.ADRDetail.SalesOrderDetail) {
                    syncSalesOrderResponse(data.AdrResponse, order);
                    OrderAdapter.toOrder(data.AdrResponse.ADRDetail.SalesOrderDetail, adr);
                } else {
                    console.log("SAP Error while calling simulate...");
                    events.publishError(events.errors.SAP, {messageString: "SAP Error while calling simulate..."});
                }
                resolve(response);
            })
            .catch((err) => {
                console.log(err);
                let data;
                if (err && err.response) data = err.response.data;
                if (data && data.messages) {
                    if ((data.messages[0].type === 'OrderException') && data.AdrResponse.ADRDetail && data.AdrResponse.ADRDetail.SalesOrderDetail) {
                        syncSalesOrderResponse(data.AdrResponse, order);
                        OrderAdapter.toOrder(data.AdrResponse.ADRDetail.SalesOrderDetail, adr);
                    } else if (data.messages[0].type === 'ProductDiscontinuedException') {
                        handleDiscontinuedSku(order, adr, data.messages);
                    }
                }

                if (err.config && err.config.url) {
                    events.publishAjaxError(events.errors.ORDER_SERVICE, {
                        url: err.config.url,
                        response: err
                    });
                }
                reject(err);
            });
    });
    return promise;
};

/**
 *  Create Adr service call.  It takes the adr info collected during checkout as a param.
 * @param adr
 * @returns {*|Promise<any>}
 */
const adrCreate = (adr) => {
    let order = OrderManager.getOrder(),
        requestObject = {
            adr: {
                SalesOrder: OrderAdapter.populateSalesOrder('CREATE', adr),
                DeliveryDate: formatDateYYYYMMdd(adr.deliveryDate),
                OrderFrequency: adr.orderFrequency,
                Scan: getScanCards()
            }
        };
    let promise = new Promise(function(resolve, reject) {
        return axios({
            method: 'POST',
            url: ShopServiceApiVersion.getSapAdrServiceVersionedUrl(ConfigService.getMarketConfig().checkout.nsurl_sapAdrService),
            data: requestObject,
            params: getParams(order),
            headers: _getHeaders(),
            timeout: SERVICE_TIMEOUT
        })
            .then((response) => {
                let data = response.data;
                if (data && data.AdrResponse && data.AdrResponse.ADRDetail && data.AdrResponse.ADRDetail.SalesOrderDetail) {
                    OrderAdapter.toOrder(data.AdrResponse.ADRDetail.SalesOrderDetail, adr, true);
                    if (data.AdrResponse.redirectUrl) {
                        syncRedirectUrls(data.AdrResponse, order);
                        order.externalPayment = true;
                        ExternalPaymentService.handleExternalPayment(order, ProcessorCode.DIRECTDEBIT, null, null);
                    }
                    // Clear locally-cached ADR List
                    AdrService.clearLocalAdrList();
                    // Force a new get adr list call when visiting manage adp page to update the adr list in session storage.
                    sessionStorage.setItem(UserService.getUser().id + '-adr-list-received', 'false');
                } else {
                    console.log("SAP Error while calling create...");
                    events.publishError(events.errors.SAP, {messageString: "SAP Error while calling create..."});
                }
                resolve(response);
            })
            .catch((err) => {
                console.log(err);
                if (err.config && err.config.url) {
                    events.publishAjaxError(events.errors.ORDER_SERVICE, {
                        url: err.config.url,
                        response: err
                    });
                }
                reject(err);
            });
    });
    return promise;
};

/**
 * Make the simulate call using the current order
 * @returns {*|Promise<any>}
 */
const orderSimulate = () => {
    let order = OrderManager.getOrder(),
        postData = OrderAdapter.populateSalesOrder('SIMULATE');

    let promise = new Promise(function(resolve, reject) {
        axios({
            method: 'PUT',
            url: getResUrl(CheckoutModeService.isGuestCheckout()),
            data: postData,
            params: getParams(order),
            headers: _getHeaders(),
            timeout: SERVICE_TIMEOUT
        })
            .then((response) => {
                let data = response.data;
                if (data && data.messages) {
                    order.messages = data.messages;
                }
                if (data && data.SalesOrderResponse && data.SalesOrderResponse.SalesOrderDetail) {
                    syncSalesOrderResponse(data.SalesOrderResponse, order);
                    OrderAdapter.toOrder(data.SalesOrderResponse.SalesOrderDetail);
                } else {
                    console.log("SAP Error while calling simulate...");
                    events.publishError(events.errors.SAP, {messageString: "SAP Error while calling simulate..."});
                }
                resolve(response);
            })
            .catch((err) => {
                let data;
                console.log(err);
                if (err && err.response) data = err.response.data;
                if (data && data.messages) {
                    if ((data.messages[0].type === 'OrderException') && data.SalesOrderResponse && data.SalesOrderResponse.SalesOrderDetail) {
                        syncSalesOrderResponse(data.SalesOrderResponse, order);
                        OrderAdapter.toOrder(data.SalesOrderResponse.SalesOrderDetail);
                    } else if (data.messages[0].type === 'ProductDiscontinuedException') {
                        handleDiscontinuedSku(order, null, data.messages);
                    }
                }
                if (err.config && err.config.url) {
                    events.publishAjaxError(events.errors.ORDER_SERVICE, {
                        url: err.config.url,
                        response: err
                    });
                }
                reject(err);
            });
    });
    return promise;
};

/**
 * make the order create call using the current order
 * @returns {*|Promise<any>}
 */
const orderCreate = async () => {
    let order = OrderManager.getOrder(),
        salesOrder = OrderAdapter.populateSalesOrder('CREATE');  // Get the sales order object from the current order

    // For ship immediate the paymentauthorization and ordertotals go on the root level request object not the sales order.
    // For adr we don't pass the ordertotals or paymentauthorization
    // That is why it isn't in the populateSalesOrder function in the orderAdapter
    populateOrderTotals(order, salesOrder);
    populatePaymentAuthorization(order, salesOrder);
    try {
        const response = await axios({
            method: 'POST',
            url: getResUrl(),
            data: salesOrder,
            params: getParams(order),
            headers: _getHeaders(),
            timeout: SERVICE_TIMEOUT
        });
        let data = response.data;
        // If the payment is processing the response object does not contain the updated sales order detail
        // So we poll order status to get the updated order when the payment is processed
        if (data.processingPayment) {
            data = await pollOrderStatus(order.externalId);
        }
        if (data && data.messages) {
            order.messages = data.messages;
        }
        if (data && data.SalesOrderResponse) {
            order.paymentAuthorizationObject = data.SalesOrderResponse.paymentAuthorizationObject || null;
            syncRedirectUrls(data.SalesOrderResponse, order);
            if (data.SalesOrderResponse.SalesOrderDetail) {
                OrderAdapter.toOrder(data.SalesOrderResponse.SalesOrderDetail, null, true);
            }
        } else {
            console.log("SAP Error while calling create...");
            events.publishError(events.errors.SAP, {messageString: "SAP Error while calling create..."});
        }
        return response;
    } catch(err) {
        console.log(err);
        if (err.config && err.config.url) {
            events.publishAjaxError(events.errors.ORDER_SERVICE, {url: err.config.url, response: err});
        }
        throw err;
    }
};

const _getOrderStatusUrl = (externalId) => {
    const nsConfig = ConfigService.getMarketConfig();
    return `${nsConfig.awsUrl}/order/v1/${externalId}`;
}

/**
 * Polls the order status endpoint until the order status is updated from PENDING
 * @param {string} externalId
 * @return {Promise<object>} OrderStatusResponse
 */
const pollOrderStatus = async (externalId) => {
    // If we fail to get order status retry 5 times in 1 second intervals
    const intrvl = 2;
    let retry = 5,
        timeout = 60;
    if (!externalId) {
        throw new Error('Cannot get the order status without an externalId');
    }
    return await new Promise((resolve, reject) => {
        // Get order status every 2 seconds until the status changes from PENDING
        const interval = setInterval(async () => {
            if (timeout === 0) {
                clearInterval(interval);
                console.error(`Order status was not updated after ${timeout*2} seconds`);
                reject(new Error('Timed out polling order status'));
            }
            timeout -= intrvl;
            try {
                const response = await axios.get(_getOrderStatusUrl(externalId), {headers: _getHeaders(true)});
                const responseObj = response && response.data;
                const salesOrderResponse = responseObj && responseObj.SalesOrderResponse;
                const status = salesOrderResponse &&
                    salesOrderResponse.SalesOrderDetail &&
                    salesOrderResponse.SalesOrderDetail.Status;

                // Status changed if it is no longer in the pending state
                if (status && status !== OrderStatusEnum.PENDING) {
                    clearInterval(interval);
                    resolve(responseObj);
                }
            } catch (err) {
                if (retry === 0) {
                    clearInterval(interval);
                    reject(err);
                }
                retry--;
            }
        }, intrvl * 1000);
    });
}

export default {
    _getHeaders,
    adrShipImmediateSimulate,
    adrShipImmediateCreate,
    adrSimulate,
    adrCreate,
    orderSimulate,
    orderCreate,
    pollOrderStatus
}
